
import React, { useState, useMemo } from 'react';
import Layout from '../components/Layout';
import { COURSES } from '../data/courses';
import { Card, Button, ProgressBar, Badge } from '../components/ui';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { CourseProgress } from '../types';
import { generateCertificateHtml } from '../utils/certificate';
import { SystemCheckFlow, SystemCheckinData } from '../components/SystemCheckFlow';
import { SystemDiagnostic } from '../components/SystemDiagnostic';
import { db } from '../firebase';
import { doc, updateDoc, arrayUnion } from 'firebase/firestore';

// Define the logical roadmap categories
const CATEGORY_MAP: Record<string, string> = {
  // The Foundation
  'mindset_shift': 'The Foundation: Your Mindset',
  'family_stability_blueprint': 'The Foundation: Your Mindset',
  'dadfoundry_blueprint': 'The Foundation: Your Mindset',
  'the_fatherhood_blueprint': 'The Foundation: Your Mindset',

  // Step 1: Starting the Process
  'children_act': 'Step 1: Starting the Process',
  'miam_mediation': 'Step 1: Starting the Process',
  'c100_application': 'Step 1: Starting the Process',
  'representing_yourself': 'Step 1: Starting the Process',
  'direct_access': 'Step 1: Starting the Process',
  
  // Step 2: Preparing Your Evidence
  'legal_standards': 'Step 2: Preparing Your Evidence',
  'disclosure_discovery': 'Step 2: Preparing Your Evidence',
  'position_statements': 'Step 2: Preparing Your Evidence',
  'witness_statements': 'Step 2: Preparing Your Evidence',
  'digital_bundle': 'Step 2: Preparing Your Evidence',
  'cafcass': 'Step 2: Preparing Your Evidence',
  
  // Step 3: The Courtroom Path
  'urgent_hearings': 'Step 3: The Courtroom Path',
  'fhdra_basics': 'Step 3: The Courtroom Path',
  'fact_finding': 'Step 3: The Courtroom Path',
  'dra_tactics': 'Step 3: The Courtroom Path',
  'final_hearing': 'Step 3: The Courtroom Path',
  
  // Step 4: Managing Complex Issues
  'enforcement': 'Step 4: Managing Complex Issues',
  'challenging_s7': 'Step 4: Managing Complex Issues',
  'false_allegations': 'Step 4: Managing Complex Issues',
  'non_molestation': 'Step 4: Managing Complex Issues',
  'harassment_boundaries': 'Step 4: Managing Complex Issues',
  'alienating_behaviours': 'Step 4: Managing Complex Issues',
  
  // Step 5: Parenting & Wellbeing
  'co_parenting_skills': 'Step 5: Parenting & Wellbeing',
  'parallel_parenting': 'Step 5: Parenting & Wellbeing',
  'positive_parenting': 'Step 5: Parenting & Wellbeing',
  'emotional_regulation': 'Step 5: Parenting & Wellbeing',
  'newborn_operations_manual': 'Step 5: Parenting & Wellbeing',

  // Step 6: Finances & Support
  'child_maintenance': 'Step 6: Finances & Support',
  'schedule_1': 'Step 6: Finances & Support',
  'grandparents': 'Step 6: Finances & Support',
};

const Dashboard = () => {
  const { currentUser, userProfile, schoolProfile, refreshProfile } = useAuth();
  const navigate = useNavigate();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'action' | 'compliant'>('all');
  const [activeTrack, setActiveTrack] = useState<'legal' | 'family'>('family');
    // Thermal Checkin State
    const [showThermometer, setShowThermometer] = useState(false);
    const [latestCheckinData, setLatestCheckinData] = useState<SystemCheckinData | null>(null);
    const [showIntervention, setShowIntervention] = useState(false);

    const handleSystemCheckComplete = async (data: SystemCheckinData) => {
        if (!userProfile) return;

        try {
            const userRef = doc(db, 'users', userProfile.uid);
            await updateDoc(userRef, {
                thermalCheckins: arrayUnion({
                    id: Date.now().toString(),
                    timestamp: new Date().toISOString(),
                    ...data
                })
            });
            await refreshProfile();
            setLatestCheckinData(data);
            setShowThermometer(false);
            setShowIntervention(true); // Show Phase 3 & 4
        } catch (error) {
            console.error('Error saving system checkin:', error);
        }
    };

  const checkExpiry = (progress?: CourseProgress) => {
    if (!progress?.quizPassed) return false;
    const dateStr = progress.dateCompleted || progress.lastAccessed;
    if (!dateStr) return false;
    const completedDate = new Date(dateStr).getTime();
    const now = new Date().getTime();
    const ONE_YEAR_MS = 365 * 24 * 60 * 60 * 1000;
    return (now - completedDate) > ONE_YEAR_MS;
  };

  const formatDateShort = (dateVal: any) => {
    if (!dateVal) return '';
    try {
        const d = dateVal.toDate ? dateVal.toDate() : new Date(dateVal);
        if (isNaN(d.getTime())) return '';
        return d.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch (e) { return ''; }
  };

  const processedCourses = useMemo(() => {
    if (!userProfile) return [];

    return COURSES.map(course => {
        const userProgress = userProfile.progress?.[course.id];
        const isExpired = checkExpiry(userProgress);
        
        let status: 'not_started' | 'in_progress' | 'compliant' | 'failed' | 'expired' = 'not_started';
        let completionDateDisplay = null;

        if (isExpired) {
            status = 'expired';
        } else if (userProgress?.quizPassed) {
            status = 'compliant';
            completionDateDisplay = formatDateShort(userProgress.dateCompleted || userProgress.lastAccessed);
        } else if (userProgress && userProgress.quizPassed === false && userProgress.quizScore !== undefined) {
            status = 'failed';
        } else if (userProgress && userProgress.completedModules?.length > 0) {
            status = 'in_progress';
        }

        const modulesCompleted = (status === 'not_started' || status === 'expired') ? 0 : (userProgress?.completedModules?.length || 0);
        let displayPercent = (modulesCompleted / course.totalModules) * 100;
        if (status === 'compliant') displayPercent = 100;

        let badgeColor: 'blue' | 'green' | 'red' | 'yellow' = 'blue';
        let badgeText = 'Start';
        let progressColor = "bg-md-primary";
        let actionText = "Start";

        switch(status) {
            case 'compliant':
                badgeColor = 'green'; badgeText = 'Completed'; progressColor = "bg-md-success"; actionText = "Review"; break;
            case 'expired':
                badgeColor = 'red'; badgeText = 'Expired'; progressColor = "bg-md-error"; actionText = "Renew"; break;
            case 'failed':
                badgeColor = 'red'; badgeText = 'Retake'; progressColor = "bg-md-error"; actionText = "Retake"; break;
            case 'in_progress':
                badgeColor = 'yellow'; badgeText = 'In Progress'; progressColor = "bg-md-primary"; actionText = "Continue"; break;
            default:
                badgeColor = 'blue'; badgeText = 'To Do'; progressColor = "bg-md-surface-variant"; actionText = "Start";
        }

        return {
            ...course,
            status,
            displayPercent,
            badgeColor,
            badgeText,
            progressColor,
            actionText,
            completionDateDisplay,
            category: CATEGORY_MAP[course.id] || 'General Training'
        };
    });
  }, [userProfile]);

  const recentCourse = useMemo(() => {
    if (!userProfile?.recentCourseId) return null;
    return processedCourses.find(c => c.id === userProfile.recentCourseId && c.status === 'in_progress');
  }, [userProfile?.recentCourseId, processedCourses]);

  const filteredCourses = processedCourses.filter(c => {
      const matchesSearch = c.title.toLowerCase().includes(searchTerm.toLowerCase()) || 
                            c.description.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesFilter = filterStatus === 'all' ? true : filterStatus === 'compliant' ? c.status === 'compliant' : (c.status !== 'compliant'); 
      return matchesSearch && matchesFilter;
  });

  const groupedCourses = filteredCourses.reduce((acc, course) => {
      if (!acc[course.category]) acc[course.category] = [];
      acc[course.category].push(course);
      return acc;
  }, {} as Record<string, typeof processedCourses>);

  const TRACK_CATEGORIES = {
      legal: [
        'Step 1: Starting the Process', 
        'Step 2: Preparing Your Evidence', 
        'Step 3: The Courtroom Path', 
        'Step 4: Managing Complex Issues', 
      ],
      family: [
        'The Foundation: Your Mindset',
        'Step 5: Parenting & Wellbeing',
        'Step 6: Finances & Support'
      ]
  };

  const sortedCategories = TRACK_CATEGORIES[activeTrack].filter(cat => groupedCourses[cat] && groupedCourses[cat].length > 0);

  const totalCourses = COURSES.length;
  const completedCount = processedCourses.filter(c => c.status === 'compliant').length;
  const overallProgress = (completedCount / totalCourses) * 100;

  return (
    <Layout>      
      {showThermometer && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-md-on-surface/40 backdrop-blur-sm">
               <SystemCheckFlow 
                   onComplete={handleSystemCheckComplete}
                   onCancel={() => setShowThermometer(false)}
               />
          </div>
      )}

      {showIntervention && latestCheckinData && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-md-on-surface/40 backdrop-blur-sm">
              <SystemDiagnostic 
                  data={latestCheckinData} 
                  onClose={() => setShowIntervention(false)} 
              />
          </div>
      )}
    {userProfile?.isBuddy && userProfile?.buddyProfile?.status === 'pending' && (
        <Card className="mb-6 bg-md-tertiary-container text-md-on-tertiary-container border-0 shadow-md animate-slide-up flex flex-col md:flex-row items-center justify-between p-6 gap-4">
            <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-white/30 rounded-full flex items-center justify-center text-2xl shrink-0">🤝</div>
                <div>
                    <h3 className="font-bold text-lg mb-1">You've been invited to be a Dads Matter Buddy!</h3>
                    <p className="opacity-90 text-sm">We believe your experience could help other dads currently in crisis. Set up your Buddy Profile in your settings to get started.</p>
                </div>
            </div>
            <Button onClick={() => navigate('/settings')} className="bg-md-tertiary hover:bg-md-tertiary/90 text-md-on-tertiary shadow-sm border-none whitespace-nowrap !px-6">Accept Invite</Button>
        </Card>
      )}

      <div className="mb-8 animate-slide-up flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <h2 className="text-4xl font-heading font-black tracking-tight text-md-on-surface mb-2">Conflict free roadmap</h2>
          <p className="text-md-on-surface-variant font-medium text-lg">Follow the steps below and work towards a more Child Focused future.</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 items-stretch md:items-center">
            {/* M3 Extended FABs */}
            <button 
                onClick={() => setShowThermometer(true)} 
                className="group relative bg-md-primary text-md-on-primary hover:shadow-md px-6 py-4 rounded-[20px] transition-all active:scale-95 flex items-center gap-3 w-full sm:w-auto"
            >
                <div className="text-2xl relative z-10">🌡️</div>
                <div className="text-left relative z-10 pr-2">
                    <div className="font-bold text-sm tracking-wide">System Check</div>
                    {userProfile?.thermalCheckins && userProfile.thermalCheckins.length > 0 ? (
                        <div className="text-[10px] text-md-primary-container font-black tracking-widest uppercase">
                            🔥 {Math.min(3, userProfile.thermalCheckins.length)}-Day Streak
                        </div>
                    ) : (
                        <div className="text-[10px] text-md-primary-container/80 font-medium tracking-widest uppercase">Takes 60 Seconds</div>
                    )}
                </div>
            </button>
            <button 
                onClick={() => navigate('/buddy-matcher')} 
                className="group bg-md-secondary-container hover:bg-slate-300 text-md-on-secondary-container px-6 py-4 rounded-[20px] shadow-sm hover:shadow-md transition-all active:scale-95 flex items-center gap-3 w-full sm:w-auto"
            >
                <div className="text-2xl drop-shadow-sm">🤝</div>
                <div className="text-left pr-2">
                    <div className="font-bold text-sm tracking-wide">Request Support</div>
                    <div className="text-[10px] text-md-on-secondary-container/70 font-bold tracking-widest uppercase">Direct Peer Help</div>
                </div>
            </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10 animate-slide-up delay-100">
        <div className="md:col-span-2 bg-md-primary rounded-[32px] p-8 md:p-10 text-md-on-primary shadow-sm relative overflow-hidden">
            <div className="relative z-10">
                <h3 className="text-md-on-primary/80 font-bold mb-1 tracking-wider uppercase text-sm">Roadmap Progress</h3>
                <p className="text-5xl md:text-6xl font-black tracking-tight">{Math.round(overallProgress)}%</p>
                <div className="mt-6"><ProgressBar percentage={overallProgress} color="bg-white" height="h-4" /></div>
                <p className="mt-4 text-sm text-md-on-primary/90 font-medium">{completedCount} of {totalCourses} steps completed.</p>
            </div>
            {/* Decorative Shape */}
            <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-white/10 rounded-full blur-[80px] -translate-y-1/2 translate-x-1/3"></div>
        </div>
        <div className="grid grid-rows-2 gap-6">
            <Card className="flex items-center justify-between p-6 bg-md-secondary-container text-md-on-secondary-container border-0 h-full">
                <div><p className="opacity-80 font-bold text-[10px] mb-1 uppercase tracking-widest">Topics Mastered</p><p className="text-4xl font-black tracking-tight">{completedCount}</p></div>
                <div className="w-14 h-14 bg-md-surface rounded-full flex items-center justify-center text-2xl shadow-sm">🏆</div>
            </Card>
            <Card className="flex items-center justify-between p-6 bg-md-surface-variant text-md-on-surface-variant border-0 h-full">
                <div><p className="opacity-80 font-bold text-[10px] mb-1 uppercase tracking-widest">Remaining</p><p className="text-4xl font-black tracking-tight">{totalCourses - completedCount}</p></div>
                <div className="w-14 h-14 bg-white rounded-full flex items-center justify-center text-2xl shadow-sm">🎯</div>
            </Card>
        </div>
      </div>

      {recentCourse && (
        <Card className="mb-10 bg-md-secondary-container text-md-on-secondary-container border-0 shadow-sm animate-slide-up flex flex-col md:flex-row items-center justify-between p-6 gap-6">
             <div>
                <div className="flex items-center gap-3 mb-1">
                    <span className="text-3xl">{recentCourse.icon}</span>
                    <h3 className="font-heading font-black text-xl tracking-tight">Pick up where you left off</h3>
                </div>
                <p className="text-sm opacity-90 font-medium">You were recently learning about <strong>{recentCourse.title}</strong>.</p>
             </div>
             <Button onClick={() => navigate(`/course/${recentCourse.id}`)} className="whitespace-nowrap bg-md-secondary hover:bg-slate-300 text-md-on-secondary !px-8 h-12">Resume Learning</Button>
        </Card>
      )}

      {/* Sticky Filters using M3 surface containers */}
      <div className="sticky top-0 z-20 py-4 bg-md-surface-container-lowest/80 backdrop-blur-md animate-slide-up delay-200">
          <div className="bg-md-surface-container-low p-3 rounded-[24px] shadow-sm flex flex-col xl:flex-row gap-3 justify-between items-center transition-all">
             {/* Track Selector Tabs */}
             <div className="flex bg-md-surface p-1.5 rounded-full shadow-sm w-full xl:w-auto overflow-x-auto custom-scrollbar">
                 <button onClick={() => setActiveTrack('legal')} className={`px-5 py-2.5 whitespace-nowrap rounded-full text-xs font-bold transition-all ${activeTrack === 'legal' ? 'bg-md-primary text-md-on-primary shadow-sm' : 'text-md-on-surface-variant hover:bg-md-surface-variant'}`}>⚖️ Legal Navigator</button>
                 <button onClick={() => setActiveTrack('family')} className={`px-5 py-2.5 whitespace-nowrap rounded-full text-xs font-bold transition-all ${activeTrack === 'family' ? 'bg-md-tertiary text-md-on-tertiary shadow-sm' : 'text-md-on-surface-variant hover:bg-md-surface-variant'}`}>🏠 Family & Parenting</button>
             </div>

             <div className="relative w-full xl:w-64 order-first xl:order-none">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-md-on-surface-variant text-lg">🔍</span>
                <input type="text" placeholder="Search topics..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full pl-12 pr-4 py-3 bg-md-surface text-md-on-surface border-0 rounded-full focus:ring-2 focus:ring-md-primary font-medium text-sm shadow-sm transition-all outline-none" />
             </div>

             <div className="flex bg-md-surface p-1.5 rounded-full shadow-sm w-full xl:w-auto">
                 <button onClick={() => setFilterStatus('all')} className={`flex-1 xl:flex-none px-5 py-2.5 rounded-full text-xs font-bold transition-colors ${filterStatus === 'all' ? 'bg-md-secondary-container text-md-on-secondary-container shadow-sm' : 'text-md-on-surface-variant hover:bg-md-surface-variant'}`}>All</button>
                 <button onClick={() => setFilterStatus('action')} className={`flex-1 xl:flex-none px-5 py-2.5 rounded-full text-xs font-bold transition-colors ${filterStatus === 'action' ? 'bg-md-error-container text-md-on-error-container shadow-sm' : 'text-md-on-surface-variant hover:bg-md-surface-variant'}`}>To Do</button>
                 <button onClick={() => setFilterStatus('compliant')} className={`flex-1 xl:flex-none px-5 py-2.5 rounded-full text-xs font-bold transition-colors ${filterStatus === 'compliant' ? 'bg-md-primary-container text-md-on-primary-container shadow-sm' : 'text-md-on-surface-variant hover:bg-md-surface-variant'}`}>Done</button>
             </div>
          </div>
      </div>
      
      <div className="space-y-14 pb-12 animate-slide-up delay-300 mt-6">
        {sortedCategories.map((category, index) => (
            <div key={category}>
                <div className="flex items-center gap-4 mb-8 mt-4">
                    <div className="w-10 h-10 rounded-full bg-md-primary text-md-on-primary flex items-center justify-center text-sm font-black shadow-sm">{category.includes('Foundation') ? '★' : index}</div>
                    <h3 className="text-2xl font-black font-heading tracking-tight text-md-on-surface">{category}</h3>
                    <div className="h-px bg-md-outline-variant flex-1 ml-4 opacity-50"></div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                    {groupedCourses[category].map(course => (
                        <div key={course.id} onClick={() => navigate(`/course/${course.id}`)} className="group cursor-pointer flex">
                            <Card hover className="flex-1 flex flex-col relative overflow-hidden bg-md-surface-container-low border-0 transition-colors hover:bg-md-surface-variant p-0">
                                {/* M3 styled indicator bar on top instead of bg-indigo-600 */}
                                <div className={`absolute top-0 left-0 w-full h-1.5 ${course.progressColor} opacity-80`}></div>
                                <div className="p-5 flex-1 mt-1">
                                    <div className="flex justify-between items-start mb-5">
                                        <div className="w-12 h-12 rounded-2xl bg-md-surface flex items-center justify-center text-2xl shadow-sm transition-transform group-hover:scale-110 duration-300">{course.icon}</div>
                                        <Badge color={course.badgeColor}>{course.badgeText}</Badge>
                                    </div>
                                    <h4 className="font-bold text-lg text-md-on-surface mb-2 font-heading tracking-tight group-hover:text-md-primary transition-colors line-clamp-2 leading-tight">{course.title}</h4>
                                    <p className="text-sm text-md-on-surface-variant line-clamp-2 leading-relaxed font-medium">{course.description}</p>
                                </div>
                                <div className="px-5 pb-5 mt-auto">
                                    <div className="flex justify-between text-[10px] font-bold text-md-on-surface-variant mb-1.5 uppercase tracking-widest">
                                        <span>{course.status === 'compliant' && course.completionDateDisplay ? `Done: ${course.completionDateDisplay}` : 'Progress'}</span>
                                        <span>{Math.round(course.displayPercent)}%</span>
                                    </div>
                                    {/* Wrapping the progressbar to constrain its height/color properly if needed */}
                                    <div className="w-full bg-md-surface-container-highest rounded-full h-1.5 overflow-hidden">
                                        <div className={`${course.progressColor} h-1.5 rounded-full transition-all duration-500`} style={{ width: `${course.displayPercent}%` }}></div>
                                    </div>
                                </div>
                            </Card>
                        </div>
                    ))}
                </div>
            </div>
        ))}
      </div>
    </Layout>
  );
};

export default Dashboard;
